import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, X, CheckCircle } from "lucide-react";
import api from "../../../../../const/axios";

interface VerifiedUser {
  id: number;
  name: string;
  surname: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmitted: () => void;
}

const VerificationDialog: React.FC<Props> = ({ open, onOpenChange, onSubmitted }) => {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<VerifiedUser[]>([]);
  const [searching, setSearching] = useState(false);
  const [ref1, setRef1] = useState<VerifiedUser | null>(null);
  const [ref2, setRef2] = useState<VerifiedUser | null>(null);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!open) {
      setSearch(""); setResults([]); setRef1(null); setRef2(null);
      setNote(""); setError("");
    }
  }, [open]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (search.trim().length < 2) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get(`verification-requests/verified-users?search=${encodeURIComponent(search.trim())}`);
        setResults(res.data ?? []);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 300);
  }, [search]);

  const select = (u: VerifiedUser) => {
    if (ref1?.id === u.id || ref2?.id === u.id) return;
    if (!ref1) { setRef1(u); setSearch(""); setResults([]); return; }
    if (!ref2) { setRef2(u); setSearch(""); setResults([]); return; }
  };

  const remove = (slot: 1 | 2) => slot === 1 ? setRef1(null) : setRef2(null);

  const submit = async () => {
    if (!ref1 || !ref2) { setError("İki zəmanətçi seçin."); return; }
    setSubmitting(true);
    setError("");
    try {
      await api.post("verification-requests", {
        referee1_ID: ref1.id,
        referee2_ID: ref2.id,
        note: note.trim() || null,
      });
      onSubmitted();
      onOpenChange(false);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? "Xəta baş verdi.");
    } finally {
      setSubmitting(false);
    }
  };

  const selectedIds = new Set([ref1?.id, ref2?.id].filter(Boolean));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Təsdiq müraciəti</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-gray-500 dark:text-gray-400">
          İki təsdiqlənmiş istifadəçi seçin. Onlar elektron poçt vasitəsilə zəmanət verməlidir.
        </p>

        {/* Selected references */}
        <div className="space-y-2">
          {([ref1, ref2] as const).map((ref, i) =>
            ref ? (
              <div key={i} className="flex items-center justify-between rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-2 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                  <span className="font-medium text-gray-900 dark:text-gray-50">{ref.name} {ref.surname}</span>
                  <span className="text-xs text-gray-400">Zəmanətçi {i + 1}</span>
                </div>
                <button onClick={() => remove((i + 1) as 1 | 2)} className="text-gray-400 hover:text-red-500 transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div key={i} className="rounded-xl border border-dashed border-gray-200 dark:border-gray-700 px-3 py-2 text-sm text-gray-400 dark:text-gray-500">
                Zəmanətçi {i + 1} — seçilməyib
              </div>
            )
          )}
        </div>

        {/* Search */}
        {(!ref1 || !ref2) && (
          <div className="relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="İstifadəçi axtar..."
                className="pl-9"
              />
            </div>
            {(results.length > 0 || searching) && (
              <div className="absolute z-10 mt-1 w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg overflow-hidden">
                {searching ? (
                  <div className="px-4 py-3 text-sm text-gray-400">Axtarılır...</div>
                ) : results.length === 0 ? (
                  <div className="px-4 py-3 text-sm text-gray-400">Nəticə tapılmadı</div>
                ) : (
                  results.map((u) => {
                    const isSelected = selectedIds.has(u.id);
                    return (
                      <button
                        key={u.id}
                        disabled={isSelected}
                        onClick={() => select(u)}
                        className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <span className="font-medium text-gray-900 dark:text-gray-50">{u.name} {u.surname}</span>
                        {isSelected && <span className="ml-2 text-xs text-gray-400">Seçilib</span>}
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>
        )}

        {/* Optional note */}
        <div>
          <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1 block">
            Qeyd <span className="font-normal">(istəyə görə, maks. 300 simvol)</span>
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={300}
            rows={3}
            placeholder="Özünüz haqqında qısa məlumat..."
            className="w-full text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/40"
          />
          <p className="text-right text-xs text-gray-400 mt-0.5">{note.length}/300</p>
        </div>

        {error && <p className="text-sm text-red-500">{error}</p>}

        <Button onClick={submit} disabled={submitting || !ref1 || !ref2} className="w-full">
          {submitting ? "Göndərilir..." : "Müraciət et"}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default VerificationDialog;
